import React, { useState } from 'react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

/**
 * OnboardingChecklist — Walter 2026-05-31. A calm, always-visible
 * (until done/dismissed) checklist at the top of the desktop sidebar
 * that guides new users through the few things worth doing, then
 * fades into feature tips.
 *
 * Motivation: a tester (Marco) tried listicity as a guest, found no
 * way to install the app (no prompt fired, nothing in Settings),
 * and gave up. Verified that install isn't BLOCKED for guests — it's
 * a discoverability failure: the install banner + Settings entry only
 * appear when the browser has fired `beforeinstallprompt`. This
 * checklist gives an always-present install path with manual
 * Add-to-Home-Screen instructions as a fallback, so the option is
 * never invisible.
 *
 * Steps shown (each auto-checks off; whole panel dismissible forever):
 *   - Sign in        (done when not anonymous)
 *   - Install the app (until running standalone; click → native prompt
 *                      if available, else manual instructions)
 *   - then tips for signed-in users with the action still undone:
 *       Try a reminder  (until they've set one)
 *       Share a list    (until they've shared one)
 *
 * Reconciled with the GuestUpgradeHint top banner (which was pulled
 * back to ONLY the "your reminders won't fire" case) so the two don't
 * double-nag about signing in. This is the gentle persistent guide;
 * the banner is the sharp, specific warning.
 *
 * Dismissal is localStorage-backed (works for guest + signed-in;
 * device-local is the right scope for an onboarding nudge).
 *
 * Desktop sidebar only for v1 — mobile's compact nav has no room.
 */

// Bumped to v2 (Walter 2026-05-31): the checklist was substantially
// reworked (trimmed to sign-in + install, friendly warn-on-dismiss,
// truthful Settings install path). Anyone who dismissed the older,
// "overwhelming" version still had `lists_onboarding_dismissed=1` in
// localStorage, which suppressed the new one forever. New key shows the
// improved checklist once more; re-dismissing writes the v2 flag.
const DISMISS_KEY = 'lists_onboarding_dismissed_v2';

// Walter 2026-06-06: "make it yours" step checks off once the user has
// opened Settings from it (so it doesn't nag forever).
const MADE_YOURS_KEY = 'lists_onboarding_madeyours_v1';

// Walter 2026-06-16: "keyboard shortcuts" step checks off once the user has
// opened the cheat-sheet from it (so it doesn't nag forever).
const SHORTCUTS_VIEWED_KEY = 'lists_onboarding_shortcuts_v1';

export default function OnboardingChecklist({
  isAnonymous,
  remindersCount = 0,
  hasShared = false,
  onRequestSignIn,
  // Walter 2026-06-06: opens the Settings modal. When provided, adds a
  // "make it yours" step that deep-links into Settings (theme, layout,
  // font, reminders) so new users discover the personalization controls.
  onOpenSettings,
  // Walter 2026-06-13 (s2): reminder discovery step. onTryReminderList creates
  // a reminder list in one click; the step checks off once a reminder is
  // actually set (remindersCount > 0), not just when a list exists.
  onTryReminderList,
  // Walter 2026-06-16: step 5 — opens the keyboard cheat-sheet (the `?`
  // overlay). DESKTOP ONLY: the parent passes this only from the desktop
  // sidebar (mobile has no keyboard), so the step is absent on mobile.
  // Checks off once viewed, like "make it yours".
  onShowShortcuts,
}) {
  const { canInstall, isIOS, isStandalone, prompt } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [madeYours, setMadeYours] = useState(() => {
    try { return localStorage.getItem(MADE_YOURS_KEY) === '1'; } catch { return false; }
  });
  const markMadeYours = () => {
    try { localStorage.setItem(MADE_YOURS_KEY, '1'); } catch {}
    setMadeYours(true);
  };
  const [shortcutsViewed, setShortcutsViewed] = useState(() => {
    try { return localStorage.getItem(SHORTCUTS_VIEWED_KEY) === '1'; } catch { return false; }
  });
  const markShortcutsViewed = () => {
    try { localStorage.setItem(SHORTCUTS_VIEWED_KEY, '1'); } catch {}
    setShortcutsViewed(true);
  };
  // Walter 2026-05-31 (v2): dismissing now requires an explicit
  // acknowledgement instead of vanishing (or auto-fading) on ×. Clicking
  // × swaps the panel for a short explanation with two real buttons —
  // "keep onboarding info" / "hide onboarding info" — so the user
  // actively chooses, and learns install lives in Settings afterward.
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);

  // Walter 2026-05-31: feature tips (try a reminder / share a list)
  // deferred — the fuller list read as overwhelming. Keeping the panel
  // to just the core (sign in + install). Flip SHOW_TIPS back on to
  // restore them (one day, ideally one tip at a time).
  const SHOW_TIPS = false;

  const handleInstall = async () => {
    if (canInstall && prompt) {
      try { await prompt(); } catch { /* user dismissed native prompt */ }
      return;
    }
    // No native prompt available (Safari/iOS, or beforeinstallprompt
    // never fired) — reveal manual instructions instead of a dead end.
    setShowInstallHelp(v => !v);
  };

  // Build the step list. Each: { key, label, done, action?, actionLabel?, hint? }
  const steps = [];
  steps.push({
    key: 'signin',
    label: 'Sign in to save your work',
    done: !isAnonymous,
    action: isAnonymous ? onRequestSignIn : null,
    actionLabel: 'sign in',
  });
  if (!isStandalone) {
    steps.push({
      key: 'install',
      label: 'Install the app',
      done: false,
      action: handleInstall,
      actionLabel: 'install',
    });
  }
  // Make it yours → opens Settings (theme, layout, font, reminders).
  // Checks off once they've opened it from here. Only added when the
  // parent wired onOpenSettings.
  if (onOpenSettings) {
    steps.push({
      key: 'customize',
      label: 'Make it yours',
      done: madeYours,
      action: () => { markMadeYours(); onOpenSettings(); },
      actionLabel: 'customize',
    });
  }
  // Walter 2026-06-13 (s2): reminder lists are a key feature but testers
  // found them hard to discover. A real step (not just a tip) that creates
  // one in a click. Shown to everyone — a guest can try it; the guest
  // banner explains that firing needs sign-in. Checks off once they have a
  // reminder list.
  if (onTryReminderList) {
    // Walter 2026-06-15: completes when the user actually SETS a reminder
    // (remindersCount > 0), not merely when a reminder list exists — on a
    // fresh account the organizer board already ships a reminder list, which
    // wrongly pre-checked the old `hasReminderList` version. Clicking still
    // creates a reminder list to type in. Labelled "Set up a reminder".
    steps.push({
      key: 'reminderlist',
      label: 'Set up a reminder',
      done: remindersCount > 0,
      action: onTryReminderList,
      // Walter 2026-06-16: shortened "add one" → "add" so the longer
      // "Set up a reminder" label fits on one line (the wider button was
      // forcing a wrap). Dropped the bell emoji — in this narrow mono
      // button it's wide enough to bring the wrap back.
      actionLabel: 'add',
      // Walter 2026-06-15: subtext removed — label + button is self-explanatory.
    });
  }
  // Walter 2026-06-16: step 5 — keyboard shortcuts. Desktop-only (parent
  // passes onShowShortcuts only from the desktop sidebar). Opens the `?`
  // cheat-sheet and checks off once viewed.
  if (onShowShortcuts) {
    steps.push({
      key: 'shortcuts',
      label: 'Keyboard shortcuts',
      done: shortcutsViewed,
      action: () => { markShortcutsViewed(); onShowShortcuts(); },
      actionLabel: 'view',
    });
  }
  // Feature tips — only once signed in, so guests aren't shown five
  // things at once. Informational (no action button); they check off
  // when the user does the thing.
  if (SHOW_TIPS && !isAnonymous) {
    if (remindersCount === 0) {
      steps.push({
        key: 'reminder',
        label: 'Try a reminder',
        done: false,
        hint: 'type "call mom tomorrow 3pm" in a reminders list',
      });
    }
    if (!hasShared) {
      steps.push({
        key: 'share',
        label: 'Share a list',
        done: false,
        hint: "invite someone from a list's ⋯ menu",
      });
    }
  }

  const incomplete = steps.filter(s => !s.done);
  const reopen = () => {
    try { localStorage.removeItem(DISMISS_KEY); } catch {}
    setDismissed(false);
    setConfirmingDismiss(false);
  };
  // Walter 2026-06-16: once dismissed there was NO way back to the
  // getting-started card (he hit this — had to clear site data). Instead
  // of vanishing entirely, leave a small "↻ getting started" reopener
  // here (above the boards list, where the card lived) so it's one click
  // to bring it back. Only worth showing if there's still something to do.
  if (dismissed) {
    if (incomplete.length === 0) return null;
    return (
      <div style={{ margin: '8px 8px 4px' }}>
        <button
          type="button"
          onClick={reopen}
          title="show the getting-started steps again"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 0',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: 'var(--text-dimmer)',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dimmer)'; }}
        >
          <span aria-hidden="true">↻</span> getting started
        </button>
      </div>
    );
  }
  // Nothing left to do AND not mid-dismiss-note → hide.
  if (incomplete.length === 0 && !confirmingDismiss) return null;

  // × → ask, don't act. Nothing is persisted until the user confirms.
  const askDismiss = () => setConfirmingDismiss(true);
  const keepOnboarding = () => setConfirmingDismiss(false);
  const confirmHide = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setDismissed(true);
  };

  // Dismiss confirmation — an explicit, must-acknowledge choice (Walter
  // 2026-05-31). Explains what hiding does + where install lives, then
  // offers two real buttons. No auto-timeout, no silent vanish.
  if (confirmingDismiss) {
    return (
      <div style={{
        margin: '8px 8px 4px',
        padding: '10px 10px 9px',
        background: 'var(--accent-wash, rgba(232,98,44,0.07))',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
      }}>
        <div style={{
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: 'var(--text-dim)',
          fontWeight: 600,
          marginBottom: 6,
        }}>
          hide getting started?
        </div>
        <div style={{
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text)',
          lineHeight: 1.5,
          marginBottom: 10,
        }}>
          This removes the getting-started steps. You can still install
          the app anytime from{' '}
          <strong style={{ fontWeight: 600 }}>Settings → Install app</strong>.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={keepOnboarding}
            style={{
              flex: 1,
              minWidth: 110,
              background: 'var(--accent)',
              border: '1px solid var(--accent)',
              color: 'var(--bg)',
              borderRadius: 'var(--radius)',
              padding: '5px 10px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            keep onboarding info
          </button>
          <button
            type="button"
            onClick={confirmHide}
            style={{
              flex: 1,
              minWidth: 110,
              background: 'transparent',
              border: '1px solid var(--border-bright)',
              color: 'var(--text-dim)',
              borderRadius: 'var(--radius)',
              padding: '5px 10px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              cursor: 'pointer',
            }}
          >
            hide onboarding info
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      margin: '8px 8px 4px',
      padding: '10px 10px 8px',
      background: 'var(--accent-wash, rgba(232,98,44,0.07))',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      fontFamily: 'var(--mono)',
      position: 'relative',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <span style={{
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: 'var(--text-dim)',
          fontWeight: 600,
        }}>
          getting started
        </span>
        <button
          type="button"
          onClick={askDismiss}
          title="hide this — you can still install from Settings"
          aria-label="dismiss getting-started"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-dimmer)', fontSize: '13px', lineHeight: 1,
            padding: 2,
          }}
        >×</button>
      </div>

      {steps.map((s, i) => (
        <div key={s.key} style={{ marginBottom: 5 }}>
          {/* Walter 2026-05-31: replaced the radio-circle indicators with
              explicit "N/total" step numbers so it reads clearly as a
              short, finite sequence. Top-aligned (alignItems flex-start)
              so the number sits on the FIRST line of a label that wraps. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
            <span style={{
              flexShrink: 0,
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              fontWeight: 600,
              lineHeight: 1.4,
              color: s.done ? 'var(--text-dimmer)' : 'var(--accent)',
            }}>
              {i + 1}/{steps.length}
            </span>
            <span style={{
              flex: 1,
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              lineHeight: 1.4,
              color: s.done ? 'var(--text-dimmer)' : 'var(--text)',
              textDecoration: s.done ? 'line-through' : 'none',
            }}>
              {s.label}
            </span>
            {!s.done && s.action && (
              <button
                type="button"
                onClick={s.action}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--accent)',
                  color: 'var(--accent)',
                  borderRadius: 'var(--radius)',
                  padding: '1px 8px',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {s.actionLabel}
              </button>
            )}
          </div>
          {!s.done && s.hint && (
            <div style={{
              marginLeft: 28,
              marginTop: 1,
              fontSize: 'calc(10px * var(--ui-scale, 1))',
              color: 'var(--text-dim)',
              lineHeight: 1.4,
            }}>
              {s.hint}
            </div>
          )}
        </div>
      ))}

      {/* Manual install instructions — shown when the native prompt
          isn't available and the user clicks "install". Fixes the
          Marco "no path to install" case. */}
      {showInstallHelp && (
        <div style={{
          marginTop: 6,
          marginLeft: 28,
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          lineHeight: 1.5,
        }}>
          {isIOS
            ? 'iPhone/iPad: tap the Share button, then "Add to Home Screen".'
            : 'Open your browser menu (⋮) and choose "Install listicity" or "Add to Home Screen". If you don\'t see it, your browser may not support installing — the site still works in a tab.'}
        </div>
      )}
    </div>
  );
}
